// Relatório de Créditos de Energia — a conta que o documento entregue ao associado faz.
//
// Responde três perguntas, mês a mês: quanto a Vigor MANDOU de energia para a UC,
// quanto dessa energia foi efetivamente COMPENSADA na fatura, e quanto SOBROU.
//
// Mora em `_shared/` porque a edge que desenha o PDF (Deno) e o modal de conferência
// (navegador) precisam do MESMO número — o mesmo motivo de `statusBoleto.ts` e
// `recebiveisUc.ts`. Duas cópias divergiriam na primeira mudança, e aqui a divergência
// sairia impressa num documento que o associado pode usar contra a Vigor.
//
//   node --experimental-strip-types --test supabase/functions/_shared/__tests__/relatorioCreditos.test.ts
//
// ── As quatro colunas do bloco SCEE ────────────────────────────────────────────
//
// `unidade_geradora_N` + `excedente_recebido_unidade_geradora_N` vêm do quadro
// "INFORMAÇÕES DO SCEE": QUEM mandou e QUANTO mandou. É a energia disponibilizada.
//
// `descricao_ug_N` + `injecao_kwh_ug_N` vêm das linhas "INJEÇÃO SCEE" dos itens da
// fatura: o que foi de fato abatido do consumo naquele ciclo. A descrição carrega a UC
// geradora no próprio texto ("INJEÇÃO SCEE - UC 000406019301238 - GD I"), e é por ela
// que se sabe de quem era o crédito compensado.
//
// ⚠️ Os índices das duas listas NÃO se correspondem: o mapeador as preenche de forma
// independente (ver mapeador-faturas/index.ts, "listas SCEE × INJEÇÃO independentes").
// Uma fatura pode ter excedente da UG nos slots 1 e 2 e uma única linha de injeção, no
// slot 1, referente à UG do slot 2 — é o caso mais comum na base. Parear por índice
// atribuiria a compensação à usina errada; por isso o pareamento é pela UC do texto.
//
// ── Por que não usar `credito_recebido` como energia compensada ───────────────
//
// `credito_recebido` é o CRÉDITO RECEBIDO do ciclo inteiro, sem distinção de origem.
// Numa UC que também recebe de geração de terceiro, usá-lo creditaria à Vigor energia
// que não é dela. Ele entra aqui só como fallback declarado, quando a fatura não traz
// nenhuma linha de injeção identificável — e nesse caso a linha é marcada.
//
// ── `saldo_ugs_vigor` não é o saldo da Vigor ──────────────────────────────────
//
// Apesar do nome, é o "SALDO KWH" impresso pela distribuidora: o estoque TOTAL de
// créditos da UC, de todas as origens. É justamente o número que o relatório contrapõe
// ao saldo da Vigor na seção "Saldo total da unidade consumidora". Ver a memória
// `saldo-credito-vs-saldo-do-mes` e o cabeçalho de `coberturaSaldo.ts`.

/** Um mês de fatura, na fatia que o relatório usa. Estrutural de propósito: o modal
 *  passa a linha do Supabase e a edge passa a dela, sem um tipo comum a importar. */
export interface FaturaRelatorioCreditos {
  id?: number | string | null;
  mes_referencia?: string | null;
  data_vencimento?: string | null;

  unidade_geradora_1?: string | null;
  unidade_geradora_2?: string | null;
  unidade_geradora_3?: string | null;
  unidade_geradora_4?: string | null;
  unidade_geradora_5?: string | null;
  excedente_recebido_unidade_geradora_1?: number | string | null;
  excedente_recebido_unidade_geradora_2?: number | string | null;
  excedente_recebido_unidade_geradora_3?: number | string | null;
  excedente_recebido_unidade_geradora_4?: number | string | null;
  excedente_recebido_unidade_geradora_5?: number | string | null;

  descricao_ug_1?: string | null;
  descricao_ug_2?: string | null;
  descricao_ug_3?: string | null;
  descricao_ug_4?: string | null;
  descricao_ug_5?: string | null;
  injecao_kwh_ug_1?: number | string | null;
  injecao_kwh_ug_2?: number | string | null;
  injecao_kwh_ug_3?: number | string | null;
  injecao_kwh_ug_4?: number | string | null;
  injecao_kwh_ug_5?: number | string | null;

  credito_recebido?: number | string | null;
  /** "SALDO KWH" da distribuidora — estoque TOTAL, não só o da Vigor. */
  saldo_ugs_vigor?: number | string | null;
  consumo_ativo_kwh?: number | string | null;
  valor_fatura_distribuidora?: number | string | null;
  valor_boleto_vigor?: number | string | null;
  economia?: number | string | null;
}

/** Por que uma linha não pode entrar nos totais. */
export type MotivoSemDado =
  /** Nenhuma UG no bloco SCEE — a fatura não registra energia disponibilizada. */
  | 'sem_excedente'
  /** Há excedente, mas nada indica quanto foi compensado no ciclo. */
  | 'sem_compensacao';

/** De onde saiu a energia compensada da linha — vai impresso, para o associado
 *  saber o peso do número que está lendo. */
export type OrigemCompensacao =
  /** Somada das linhas INJEÇÃO SCEE cuja UC é de usina da Vigor. O caso bom. */
  | 'injecao_por_uc'
  /** A fatura não identifica a UC das injeções; usou-se `credito_recebido` inteiro.
   *  Só é aceito quando TODO o excedente do mês veio da Vigor — do contrário
   *  atribuiria à Vigor crédito de terceiro. */
  | 'credito_recebido'
  /** Não deu para dizer. A linha não soma. */
  | 'indeterminada';

export interface ParcelaGeradora {
  /** UC da usina como impressa na fatura. */
  uc: string;
  /** kWh disponibilizados por ela no ciclo. */
  excedente: number;
  daVigor: boolean;
}

export interface LinhaRelatorioCreditos {
  mesReferencia: string;
  /** Ordenável: ano * 12 + mês. `null` quando o mês não é reconhecível. */
  ordem: number | null;
  dataVencimento: string | null;

  /** Energia disponibilizada no ciclo pelas usinas DA VIGOR. */
  excedenteVigor: number;
  /** Idem, por usinas de terceiro. Existe para explicar o saldo da distribuidora. */
  excedenteOutros: number;
  /** Excedente cuja usina a fatura não identifica. Não é creditado a ninguém. */
  excedenteSemOrigem: number;
  /** Energia da Vigor efetivamente compensada no ciclo. */
  compensadaVigor: number;
  origemCompensacao: OrigemCompensacao;
  /** `excedenteVigor − compensadaVigor`. Pode ser negativo quando a UC consumiu
   *  estoque de meses anteriores — e nesse caso é isso mesmo que tem de sair. */
  saldoPeriodo: number;

  /** "SALDO KWH" da distribuidora no ciclo (todas as origens). */
  saldoSceeDistribuidora: number | null;
  consumo: number | null;
  valorFaturaDistribuidora: number | null;
  valorVigor: number | null;
  economia: number | null;

  geradoras: ParcelaGeradora[];

  /** Falso quando falta dado. A linha aparece no PDF como "sem informação
   *  disponível" (decisão do Vinicius Alves, 09/09/2026) e fica fora dos totais. */
  temDado: boolean;
  motivoSemDado: MotivoSemDado | null;
}

export interface TotaisRelatorioCreditos {
  excedenteVigor: number;
  compensadaVigor: number;
  /** O saldo que o relatório afirma: enviado − compensado, sobre os meses com dado. */
  saldoVigor: number;
  excedenteOutros: number;
  economia: number;
  valorVigor: number;
}

export type AvisoRelatorio =
  | { tipo: 'meses_sem_dado'; meses: string[] }
  | { tipo: 'compensacao_por_credito_recebido'; meses: string[] }
  | { tipo: 'excedente_de_terceiro'; meses: string[]; total: number }
  | { tipo: 'saldo_distribuidora_maior'; saldoDistribuidora: number; saldoVigor: number; diferenca: number }
  | { tipo: 'meses_duplicados'; meses: string[] }
  | { tipo: 'sem_cadastro_de_usinas' }
  | { tipo: 'periodo_vazio' };

export interface RelatorioCreditos {
  linhas: LinhaRelatorioCreditos[];
  totais: TotaisRelatorioCreditos;
  avisos: AvisoRelatorio[];
  /** Último "SALDO KWH" impresso pela distribuidora no período, e de que mês veio.
   *  É o número da seção que separa o saldo total do saldo da Vigor. */
  saldoDistribuidoraFinal: { valor: number; mesReferencia: string } | null;
  /** Quantos meses entraram nos totais e quantos foram lidos. */
  mesesComDado: number;
  mesesTotal: number;
}

const MESES_ABREV = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** Número tolerante a `"1.234,56"`, `"1234.56"`, `null` e `NaN`. Devolve 0 —
 *  quem precisa distinguir "zero" de "não informado" usa `numOuNulo`. */
export function num(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v !== 'string') return 0;
  const s = v.trim();
  if (!s) return 0;
  // "1.234,56" (brasileiro) x "1234.56" (banco). A vírgula decide.
  const limpo = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  const n = parseFloat(limpo);
  return Number.isFinite(n) ? n : 0;
}

/** Como `num`, mas preserva a diferença entre "0" e "não informado". */
export function numOuNulo(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string' && !v.trim()) return null;
  const n = num(v);
  return typeof v === 'string' && !/\d/.test(v) ? null : n;
}

/** Mesma normalização de `views/Gestao/Extracao/planilha/faturaGridModel.ts`:
 *  a fatura imprime "000406019301238" e o cadastro guarda "4.060.193.012-38". */
export function limparUcGeradora(uc: unknown): string {
  return (typeof uc === 'string' || typeof uc === 'number' ? String(uc) : '')
    .replace(/[^0-9]/g, '')
    .replace(/^0+/, '');
}

/** 'AGO/2025' → 2025*12+7. `null` no que não for reconhecível — o relatório
 *  prefere deixar o mês no fim da lista a inventar uma data para ele. */
export function ordemDoMes(mesReferencia: unknown): number | null {
  const s = String(mesReferencia ?? '').trim().toUpperCase();
  const m = s.match(/^([A-Z]{3})\/?(\d{4})$/);
  if (!m) return null;
  const idx = MESES_ABREV.indexOf(m[1]);
  if (idx < 0) return null;
  return Number(m[2]) * 12 + idx;
}

/** A UC geradora escrita dentro da descrição da linha de injeção
 *  ("INJEÇÃO SCEE - UC 000406019301238 - GD I"). '' quando não há. */
export function ucDaDescricaoInjecao(descricao: unknown): string {
  const s = String(descricao ?? '');
  const m = s.match(/UC\s*(\d{6,})/i);
  return m ? limparUcGeradora(m[1]) : '';
}

const SLOTS = [1, 2, 3, 4, 5] as const;

/**
 * Uma linha do relatório a partir de uma fatura.
 *
 * `ucsVigor` é o conjunto de UCs das usinas da Vigor, já normalizado
 * (`rateio_unidades_geradoras.numero_uc_geradora`). Quando vem `null` — cadastro
 * vazio ou RLS barrando —, não se classifica origem: todo excedente conta como
 * da Vigor e o chamador recebe o aviso `sem_cadastro_de_usinas`. Chutar que tudo
 * é de terceiro zeraria o relatório de quem tem os dados certos.
 */
export function linhaDaFatura(
  fatura: FaturaRelatorioCreditos,
  ucsVigor: Set<string> | null
): LinhaRelatorioCreditos {
  const semCadastro = ucsVigor === null;
  const daVigor = (uc: string): boolean => (semCadastro ? true : ucsVigor!.has(uc));

  const geradoras: ParcelaGeradora[] = [];
  let excedenteVigor = 0;
  let excedenteOutros = 0;
  let excedenteSemOrigem = 0;

  for (const g of SLOTS) {
    const ucBruta = (fatura as Record<string, unknown>)[`unidade_geradora_${g}`];
    const excedente = num((fatura as Record<string, unknown>)[`excedente_recebido_unidade_geradora_${g}`]);
    const uc = limparUcGeradora(ucBruta);
    if (!uc && excedente <= 0) continue; // slot vazio do template

    if (!uc) {
      excedenteSemOrigem += excedente;
      continue;
    }
    const ehVigor = daVigor(uc);
    geradoras.push({ uc, excedente, daVigor: ehVigor });
    if (ehVigor) excedenteVigor += excedente;
    else excedenteOutros += excedente;
  }

  // Compensação: soma das injeções cuja UC é de usina da Vigor.
  let compensadaVigor = 0;
  let houveInjecaoIdentificada = false;
  let houveInjecaoAnonima = false;
  for (const g of SLOTS) {
    const qtd = Math.abs(num((fatura as Record<string, unknown>)[`injecao_kwh_ug_${g}`]));
    const descricao = (fatura as Record<string, unknown>)[`descricao_ug_${g}`];
    if (qtd <= 0 && !descricao) continue;
    const uc = ucDaDescricaoInjecao(descricao);
    if (!uc) {
      if (qtd > 0) houveInjecaoAnonima = true;
      continue;
    }
    houveInjecaoIdentificada = true;
    if (daVigor(uc)) compensadaVigor += qtd;
  }

  let origemCompensacao: OrigemCompensacao;
  if (houveInjecaoIdentificada) {
    origemCompensacao = 'injecao_por_uc';
  } else {
    // Sem injeção identificável, `credito_recebido` só pode ser creditado à Vigor
    // se não houver crédito de terceiro no ciclo para confundir com ele.
    const credito = num(fatura.credito_recebido);
    const soVigor = excedenteOutros <= 0 && excedenteSemOrigem <= 0;
    if (credito > 0 && soVigor) {
      compensadaVigor = credito;
      origemCompensacao = 'credito_recebido';
    } else if (houveInjecaoAnonima || credito > 0) {
      origemCompensacao = 'indeterminada';
    } else {
      // Ciclo sem nenhuma compensação registrada. Zero é um resultado legítimo:
      // a UC pode ter recebido excedente e não ter consumido nada dele.
      origemCompensacao = 'injecao_por_uc';
    }
  }

  const temExcedente = excedenteVigor > 0 || excedenteOutros > 0 || excedenteSemOrigem > 0;
  let motivoSemDado: MotivoSemDado | null = null;
  if (!temExcedente) motivoSemDado = 'sem_excedente';
  else if (origemCompensacao === 'indeterminada') motivoSemDado = 'sem_compensacao';

  return {
    mesReferencia: String(fatura.mes_referencia ?? '').trim(),
    ordem: ordemDoMes(fatura.mes_referencia),
    dataVencimento: fatura.data_vencimento ? String(fatura.data_vencimento) : null,
    excedenteVigor,
    excedenteOutros,
    excedenteSemOrigem,
    compensadaVigor: motivoSemDado ? 0 : compensadaVigor,
    origemCompensacao,
    saldoPeriodo: motivoSemDado ? 0 : excedenteVigor - compensadaVigor,
    saldoSceeDistribuidora: numOuNulo(fatura.saldo_ugs_vigor),
    consumo: numOuNulo(fatura.consumo_ativo_kwh),
    valorFaturaDistribuidora: numOuNulo(fatura.valor_fatura_distribuidora),
    valorVigor: numOuNulo(fatura.valor_boleto_vigor),
    economia: numOuNulo(fatura.economia),
    geradoras,
    temDado: motivoSemDado === null,
    motivoSemDado,
  };
}

/** Arredonda para 2 casas sem carregar o erro de ponto flutuante nos totais. */
const r2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Escolhe qual das duas linhas do MESMO mês representa o mês no relatório.
 *
 * A mesma UC tem, com frequência, mais de uma fatura no mesmo mês de referência
 * (ver a memória `janela-faturamento-duplicatas-uc`): a UC 404028401208 tem três
 * linhas de JUL/2026 em produção, duas delas com dados de SCEE diferentes. Somar
 * as duas dobraria o excedente do associado, então uma tem de vencer.
 *
 * ⚠️ O critério é DETERMINÍSTICO de propósito. "A última lida" dependeria da
 * ordem que o Postgres devolveu, e dois operadores gerando o mesmo relatório no
 * mesmo dia veriam totais diferentes — inaceitável num documento que vai ao
 * cliente. Quando as duas têm dado, o caso vira aviso: quem confere decide.
 */
function melhorLinha(a: LinhaRelatorioCreditos, b: LinhaRelatorioCreditos): LinhaRelatorioCreditos {
  if (a.temDado !== b.temDado) return a.temDado ? a : b;
  if (a.excedenteVigor !== b.excedenteVigor) return a.excedenteVigor > b.excedenteVigor ? a : b;
  if (a.compensadaVigor !== b.compensadaVigor) return a.compensadaVigor > b.compensadaVigor ? a : b;
  const sa = a.saldoSceeDistribuidora ?? -1;
  const sb = b.saldoSceeDistribuidora ?? -1;
  return sb > sa ? b : a;
}

/**
 * O relatório inteiro. `faturas` pode vir em qualquer ordem e com meses
 * repetidos — ver `melhorLinha` para como o mês repetido é resolvido.
 */
export function montarRelatorioCreditos(
  faturas: FaturaRelatorioCreditos[],
  ucsVigor: Set<string> | null
): RelatorioCreditos {
  const porMes = new Map<string, LinhaRelatorioCreditos>();
  /** Meses em que MAIS DE UMA fatura trouxe informação de créditos. É ambiguidade
   *  real, não duplicata boba: o operador precisa ver antes de enviar. */
  const duplicados = new Set<string>();

  for (const f of faturas) {
    const linha = linhaDaFatura(f, ucsVigor);
    const chave = linha.mesReferencia || `#${String(f.id ?? porMes.size)}`;
    const anterior = porMes.get(chave);
    if (!anterior) {
      porMes.set(chave, linha);
      continue;
    }
    if (anterior.temDado && linha.temDado) duplicados.add(chave);
    porMes.set(chave, melhorLinha(anterior, linha));
  }

  const linhas = Array.from(porMes.values()).sort((a, b) => {
    if (a.ordem === null && b.ordem === null) return a.mesReferencia.localeCompare(b.mesReferencia);
    if (a.ordem === null) return 1;
    if (b.ordem === null) return -1;
    return a.ordem - b.ordem;
  });

  const comDado = linhas.filter(l => l.temDado);
  const totais: TotaisRelatorioCreditos = {
    excedenteVigor: r2(comDado.reduce((s, l) => s + l.excedenteVigor, 0)),
    compensadaVigor: r2(comDado.reduce((s, l) => s + l.compensadaVigor, 0)),
    saldoVigor: 0,
    excedenteOutros: r2(comDado.reduce((s, l) => s + l.excedenteOutros, 0)),
    economia: r2(linhas.reduce((s, l) => s + (l.economia ?? 0), 0)),
    valorVigor: r2(linhas.reduce((s, l) => s + (l.valorVigor ?? 0), 0)),
  };
  totais.saldoVigor = r2(totais.excedenteVigor - totais.compensadaVigor);

  // O saldo da distribuidora é ESTOQUE, não fluxo: vale o do mês mais recente que
  // trouxe o número, e nunca a soma dos meses.
  let saldoDistribuidoraFinal: RelatorioCreditos['saldoDistribuidoraFinal'] = null;
  for (const l of linhas) {
    if (l.saldoSceeDistribuidora !== null) {
      saldoDistribuidoraFinal = { valor: l.saldoSceeDistribuidora, mesReferencia: l.mesReferencia };
    }
  }

  const avisos: AvisoRelatorio[] = [];
  if (linhas.length === 0) {
    avisos.push({ tipo: 'periodo_vazio' });
  }
  if (ucsVigor === null) {
    avisos.push({ tipo: 'sem_cadastro_de_usinas' });
  }
  const semDado = linhas.filter(l => !l.temDado).map(l => l.mesReferencia);
  if (semDado.length) avisos.push({ tipo: 'meses_sem_dado', meses: semDado });

  if (duplicados.size) {
    // Na ordem do relatório, não na de inserção no Set.
    const meses = linhas.map(l => l.mesReferencia).filter(m => duplicados.has(m));
    avisos.push({ tipo: 'meses_duplicados', meses });
  }

  const porCredito = comDado.filter(l => l.origemCompensacao === 'credito_recebido').map(l => l.mesReferencia);
  if (porCredito.length) avisos.push({ tipo: 'compensacao_por_credito_recebido', meses: porCredito });

  const comTerceiro = comDado.filter(l => l.excedenteOutros > 0);
  if (comTerceiro.length) {
    avisos.push({
      tipo: 'excedente_de_terceiro',
      meses: comTerceiro.map(l => l.mesReferencia),
      total: totais.excedenteOutros,
    });
  }

  if (saldoDistribuidoraFinal && saldoDistribuidoraFinal.valor > totais.saldoVigor) {
    avisos.push({
      tipo: 'saldo_distribuidora_maior',
      saldoDistribuidora: saldoDistribuidoraFinal.valor,
      saldoVigor: totais.saldoVigor,
      diferenca: r2(saldoDistribuidoraFinal.valor - totais.saldoVigor),
    });
  }

  return {
    linhas,
    totais,
    avisos,
    saldoDistribuidoraFinal,
    mesesComDado: comDado.length,
    mesesTotal: linhas.length,
  };
}

/** Texto do aviso, na linguagem do documento. Fica aqui para o modal e o PDF
 *  dizerem a mesma coisa ao operador e ao associado. */
export function textoDoAviso(aviso: AvisoRelatorio): string {
  switch (aviso.tipo) {
    case 'periodo_vazio':
      return 'Nenhuma fatura encontrada no período escolhido.';
    case 'sem_cadastro_de_usinas':
      return 'Não foi possível carregar o cadastro das usinas da Vigor. Todo o excedente do período está sendo contado como da Vigor — confira antes de enviar ao associado.';
    case 'meses_sem_dado':
      return `${aviso.meses.length} ${aviso.meses.length === 1 ? 'mês não tem' : 'meses não têm'} informação de créditos e ${aviso.meses.length === 1 ? 'sairá' : 'sairão'} no relatório como "sem informação disponível": ${aviso.meses.join(', ')}.`;
    case 'compensacao_por_credito_recebido':
      return `Em ${aviso.meses.join(', ')} a fatura não identifica a usina de origem da injeção; usou-se o crédito recebido do ciclo.`;
    case 'meses_duplicados':
      return `${aviso.meses.length === 1 ? 'O mês' : 'Os meses'} ${aviso.meses.join(', ')} ${aviso.meses.length === 1 ? 'tem' : 'têm'} mais de uma fatura com informação de créditos. O relatório usou a de maior excedente em cada um; confira antes de enviar ao associado.`;
    case 'excedente_de_terceiro':
      return `${formatarKwh(aviso.total)} do excedente do período vieram de geração que não é da Vigor e ficam fora do total da Vigor.`;
    case 'saldo_distribuidora_maior':
      return `O saldo registrado pela distribuidora (${formatarKwh(aviso.saldoDistribuidora)}) é maior que o saldo da Vigor (${formatarKwh(aviso.saldoVigor)}). A diferença de ${formatarKwh(aviso.diferenca)} é crédito de outras origens ou de ciclos anteriores.`;
  }
}

export function formatarKwh(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh`;
}

export function formatarReais(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** O que sai na célula de um mês sem dado. Uma constante para o modal e o PDF
 *  não divergirem no texto que o associado lê. */
export const SEM_INFORMACAO = 'sem informação disponível';
